import { cva } from "class-variance-authority";

/**
 * `--primary-hover` is declared by `.dark-teal` only and is not registered as a
 * Tailwind colour, so the hover value falls back to `<primary> / 0.9` — byte for
 * byte the previous `hover:bg-primary/90` behaviour — under the other four themes.
 * The var() fallback is substituted as raw tokens, which is why the alpha can live
 * inside it. No literal colour appears here.
 */
const PRIMARY_HOVER =
  "hover:bg-[hsl(var(--primary-hover,var(--primary)_/_0.9))]";

/**
 * Lives apart from `button.tsx` so that module exports a component and nothing
 * else. Mixing a component export with a value export breaks React Fast Refresh
 * for the whole file (`react-refresh/only-export-components`), which the lint
 * gate treats as an error via `--max-warnings 0`.
 */
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-display text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Solid actions carry the display face at 700 — the reference sets its
        // primary CTA bold and everything secondary at 500. Weight is the main
        // thing separating a real CTA from a chip.
        default: `bg-primary text-primary-foreground font-bold ${PRIMARY_HOVER}`,
        destructive:
          "bg-destructive text-destructive-foreground font-bold hover:bg-destructive/90",
        // A faint fill, not a hollow outline: fully transparent reads as a
        // cut-out on a dark canvas. Derived from --foreground so it tracks the
        // theme instead of hardcoding a white alpha.
        outline:
          "border border-foreground/10 bg-foreground/[0.04] font-medium hover:bg-foreground/[0.08] hover:border-foreground/20",
        secondary:
          "bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80",
        ghost: "font-medium hover:bg-accent hover:text-accent-foreground",
        link: "text-primary font-medium underline-offset-4 hover:underline",
      },
      // One step tighter than shadcn's stock scale, which this had been sitting
      // a step *above*: default was h-11 (44px) and lg h-12 (48px), touch-target
      // sizes on a desktop console where the same row can hold Validate, Sync
      // and Manage. Oversized controls read as a consumer app and crowd out the
      // data they act on, which is the thing anyone came to look at.
      //
      // 36px default keeps a comfortable click target while letting a toolbar
      // sit on one line. Anything smaller than the 32px `sm` starts failing as
      // a pointer target, so that is the floor.
      size: {
        default: "h-9 px-3.5 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);
